import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Receipt } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-green-100 text-green-800",
  cancelled: "bg-stone-200 text-stone-600",
  requested: "bg-blue-100 text-blue-800",
  held: "bg-teal-100 text-teal-800",
  captured: "bg-purple-100 text-purple-800",
  released: "bg-green-100 text-green-800",
  expired: "bg-red-100 text-red-700",
};

const StatusBadge = ({ status }) => (
  <span className={`px-2 py-0.5 text-xs rounded font-medium ${STATUS_STYLES[status] || "bg-stone-100 text-stone-600"}`}>
    {status}
  </span>
);

export const ChargesPanel = ({ booking }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showChargeForm, setShowChargeForm] = useState(false);
  const [chargeForm, setChargeForm] = useState({ amount: "", reason: "", notes: "", send_email: true });
  const [showHoldForm, setShowHoldForm] = useState(false);
  const [holdForm, setHoldForm] = useState({ amount: "", send_email: true });
  const [captureHoldId, setCaptureHoldId] = useState(null);
  const [captureForm, setCaptureForm] = useState({ amount: "", reason: "" });

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/bookings/${booking.id}/charges`, authHeaders());
      setData(res.data);
    } catch (e) {
      console.error("Error fetching charges:", e);
    } finally {
      setLoading(false);
    }
  }, [booking.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const createCharge = () => run(async () => {
    const res = await axios.post(`${API}/admin/bookings/${booking.id}/extra-charges`, {
      amount: parseFloat(chargeForm.amount),
      reason: chargeForm.reason,
      notes: chargeForm.notes,
      send_email: chargeForm.send_email,
    }, authHeaders());
    setShowChargeForm(false);
    setChargeForm({ amount: "", reason: "", notes: "", send_email: true });
    toast.success(res.data.email_sent ? "Charge created — payment link emailed to guest" : "Charge created (no email sent)");
  });

  const createHold = () => run(async () => {
    const res = await axios.post(`${API}/admin/bookings/${booking.id}/deposit-hold`, {
      amount: holdForm.amount ? parseFloat(holdForm.amount) : null,
      send_email: holdForm.send_email,
    }, authHeaders());
    setShowHoldForm(false);
    setHoldForm({ amount: "", send_email: true });
    toast.success(res.data.email_sent ? "Hold request sent to guest" : "Hold request created (no email sent)");
  });

  const captureHold = (holdId) => run(async () => {
    await axios.post(`${API}/admin/deposit-holds/${holdId}/capture`, {
      amount: parseFloat(captureForm.amount),
      reason: captureForm.reason,
    }, authHeaders());
    setCaptureHoldId(null);
    setCaptureForm({ amount: "", reason: "" });
  }, "Deposit captured — guest notified by email");

  if (loading) return <div className="p-4 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#134E4A]" /></div>;

  const charges = data?.extra_charges || [];
  const holds = data?.deposit_holds || [];
  const activeHold = holds.find((h) => ["requested", "held"].includes(h.status));

  return (
    <div className="p-4 border-b border-stone-200" data-testid="charges-panel">
      {/* Extra Charges */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium text-[#134E4A] flex items-center gap-2">
          <Receipt className="h-4 w-4" /> Extra Charges
        </h3>
        <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowChargeForm(!showChargeForm)} data-testid="add-extra-charge-btn">
          {showChargeForm ? "Cancel" : "+ Add Charge"}
        </Button>
      </div>

      {showChargeForm && (
        <div className="bg-stone-50 rounded p-3 mb-3 space-y-2" data-testid="extra-charge-form">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Amount ($)</Label>
              <Input type="number" value={chargeForm.amount} min="1" step="0.01"
                onChange={(e) => setChargeForm({ ...chargeForm, amount: e.target.value })}
                placeholder="300" data-testid="extra-charge-amount-input" />
            </div>
            <div>
              <Label className="text-xs">Reason (shown to guest)</Label>
              <Input value={chargeForm.reason}
                onChange={(e) => setChargeForm({ ...chargeForm, reason: e.target.value })}
                placeholder="Extra cleaning fee" data-testid="extra-charge-reason-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Details (optional, included in email)</Label>
            <Input value={chargeForm.notes}
              onChange={(e) => setChargeForm({ ...chargeForm, notes: e.target.value })}
              placeholder="e.g., slime on wicker table and deck, sand inside" data-testid="extra-charge-notes-input" />
          </div>
          <label className="flex items-center gap-2 text-sm text-stone-600">
            <input type="checkbox" checked={chargeForm.send_email}
              onChange={(e) => setChargeForm({ ...chargeForm, send_email: e.target.checked })}
              data-testid="extra-charge-email-toggle" />
            Email payment link to guest
          </label>
          <Button size="sm" onClick={createCharge}
            disabled={busy || !chargeForm.amount || !chargeForm.reason}
            className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white" data-testid="extra-charge-submit-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Charge"}
          </Button>
        </div>
      )}

      {charges.length === 0 ? (
        <p className="text-sm text-stone-400 mb-3">No extra charges</p>
      ) : (
        <div className="space-y-2 mb-3">
          {charges.map((c) => (
            <div key={c.id} className="flex items-center justify-between bg-stone-50 rounded p-2 text-sm" data-testid={`extra-charge-row-${c.id}`}>
              <div className="min-w-0">
                <p className="font-medium truncate">{c.reason}</p>
                <p className="text-xs text-stone-500">{c.created_at?.slice(0, 10)}{c.paid_at ? ` · paid ${c.paid_at.slice(0, 10)}` : ""}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="font-semibold">${c.amount?.toLocaleString()}</span>
                <StatusBadge status={c.status} />
                {c.status === "pending" && (
                  <>
                    <Button size="sm" variant="ghost" className="text-xs h-7 px-2" disabled={busy}
                      onClick={() => run(() => axios.post(`${API}/admin/extra-charges/${c.id}/resend`, {}, authHeaders()), "Payment link re-sent")}
                      data-testid={`extra-charge-resend-${c.id}`}>Resend</Button>
                    <Button size="sm" variant="ghost" className="text-xs h-7 px-2 text-red-600" disabled={busy}
                      onClick={() => run(() => axios.post(`${API}/admin/extra-charges/${c.id}/cancel`, {}, authHeaders()), "Charge cancelled")}
                      data-testid={`extra-charge-cancel-${c.id}`}>Cancel</Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Security Deposit Holds */}
      <div className="flex items-center justify-between mb-2 mt-4">
        <h3 className="font-medium text-[#134E4A] flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> Security Deposit Hold
        </h3>
        {!activeHold && (
          <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowHoldForm(!showHoldForm)} data-testid="request-deposit-hold-btn">
            {showHoldForm ? "Cancel" : "Request Hold"}
          </Button>
        )}
      </div>

      {showHoldForm && !activeHold && (
        <div className="bg-stone-50 rounded p-3 mb-3 space-y-2" data-testid="deposit-hold-form">
          <div>
            <Label className="text-xs">Hold amount ($ — leave blank for default based on stay length)</Label>
            <Input type="number" value={holdForm.amount} min="1" step="0.01"
              onChange={(e) => setHoldForm({ ...holdForm, amount: e.target.value })}
              placeholder={`Default: $${booking.protection_amount || 300}`} data-testid="deposit-hold-amount-input" />
          </div>
          <label className="flex items-center gap-2 text-sm text-stone-600">
            <input type="checkbox" checked={holdForm.send_email}
              onChange={(e) => setHoldForm({ ...holdForm, send_email: e.target.checked })}
              data-testid="deposit-hold-email-toggle" />
            Email hold link to guest
          </label>
          <Button size="sm" onClick={createHold} disabled={busy}
            className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white" data-testid="deposit-hold-submit-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Hold Request"}
          </Button>
        </div>
      )}

      {holds.length === 0 ? (
        <p className="text-sm text-stone-400">No deposit holds — guest's card has not been held</p>
      ) : (
        <div className="space-y-2">
          {holds.map((h) => (
            <div key={h.id} className="bg-stone-50 rounded p-2 text-sm" data-testid={`deposit-hold-row-${h.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-semibold">${h.amount?.toLocaleString()}</span>
                  {h.status === "captured" && h.captured_amount != null && (
                    <span className="text-xs text-stone-500 ml-2">captured ${h.captured_amount?.toLocaleString()}</span>
                  )}
                  <p className="text-xs text-stone-500">{h.created_at?.slice(0, 10)}{h.capture_reason ? ` · ${h.capture_reason}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={h.status} />
                  {h.status === "requested" && (
                    <>
                      <Button size="sm" variant="ghost" className="text-xs h-7 px-2" disabled={busy}
                        onClick={() => run(() => axios.post(`${API}/admin/deposit-holds/${h.id}/resend`, {}, authHeaders()), "Hold request re-sent")}
                        data-testid={`deposit-hold-resend-${h.id}`}>Resend</Button>
                      <Button size="sm" variant="ghost" className="text-xs h-7 px-2 text-red-600" disabled={busy}
                        onClick={() => run(() => axios.post(`${API}/admin/deposit-holds/${h.id}/cancel`, {}, authHeaders()), "Hold request cancelled")}
                        data-testid={`deposit-hold-cancel-${h.id}`}>Cancel</Button>
                    </>
                  )}
                  {h.status === "held" && (
                    <>
                      <Button size="sm" variant="outline" className="text-xs h-7 px-2 border-purple-400 text-purple-700" disabled={busy}
                        onClick={() => { setCaptureHoldId(captureHoldId === h.id ? null : h.id); setCaptureForm({ amount: String(h.amount), reason: "" }); }}
                        data-testid={`deposit-hold-capture-btn-${h.id}`}>Capture</Button>
                      <Button size="sm" variant="outline" className="text-xs h-7 px-2 border-green-500 text-green-700" disabled={busy}
                        onClick={() => run(() => axios.post(`${API}/admin/deposit-holds/${h.id}/release`, {}, authHeaders()), "Hold released — guest notified")}
                        data-testid={`deposit-hold-release-btn-${h.id}`}>Release</Button>
                    </>
                  )}
                </div>
              </div>
              {captureHoldId === h.id && h.status === "held" && (
                <div className="mt-2 pt-2 border-t border-stone-200 space-y-2" data-testid="deposit-capture-form">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Amount to capture (max ${h.amount})</Label>
                      <Input type="number" value={captureForm.amount} min="1" max={h.amount} step="0.01"
                        onChange={(e) => setCaptureForm({ ...captureForm, amount: e.target.value })}
                        data-testid="deposit-capture-amount-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Reason (emailed to guest)</Label>
                      <Input value={captureForm.reason}
                        onChange={(e) => setCaptureForm({ ...captureForm, reason: e.target.value })}
                        placeholder="Extra cleaning" data-testid="deposit-capture-reason-input" />
                    </div>
                  </div>
                  <p className="text-xs text-stone-500">Capturing less than the full amount automatically releases the remainder to the guest.</p>
                  <Button size="sm" onClick={() => captureHold(h.id)}
                    disabled={busy || !captureForm.amount}
                    className="bg-purple-700 hover:bg-purple-800 text-white" data-testid="deposit-capture-submit-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Capture Funds"}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
